/**
 * SolutionPage — High-conversion landing page for a single solution
 * Route: /solutions/:locale/:slug
 */

import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { T } from '../styles/tokens';
import { ListSkeleton, SkeletonBar, Button } from '../components/ui';

interface PageData {
  id: string; slug: string; template: string; hero_image: string;
  cta_url: string; cta_style: string; og_image: string;
  view_count: number; cta_clicks: number; published_at: string;
  title: string; subtitle: string; description: string;
  meta_title: string; meta_description: string;
  cta_text: string; content: string; features_json: string;
}

const BASE = '/api/v1';

function renderMarkdown(md: string): string {
  if (!md) return '';
  return md
    .replace(/^### (.+)$/gm, `<h3 style="font-size:20px;font-weight:700;color:${T.text};margin:28px 0 12px">$1</h3>`)
    .replace(/^## (.+)$/gm, `<h2 style="font-size:24px;font-weight:700;color:${T.text};margin:32px 0 16px">$1</h2>`)
    .replace(/^# (.+)$/gm, `<h1 style="font-size:28px;font-weight:800;color:${T.text};margin:36px 0 16px">$1</h1>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#c4d0e9">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, `<code style="background:${T.border};padding:2px 6px;border-radius:4px;font-size:14px">$1</code>`)
    .replace(/^\- (.+)$/gm, '<li style="margin:6px 0;padding-left:8px;color:#8B95A8">$1</li>')
    .replace(/(<li.*?<\/li>\n?)+/g, '<ul style="list-style:disc;padding-left:20px;margin:12px 0">$&</ul>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" style="color:#5B8DEF;text-decoration:underline" target="_blank">$1</a>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
}

const featureIcons = [
  'M13 2L3 14h9l-1 8 10-12h-9l1-8',
  'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10',
  'M22 11.08V12a10 10 0 11-5.93-9.14',
  'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z',
  'M18 20V10M12 20V4M6 20v-6',
];

export function SolutionPage(props: { path: string; locale?: string; slug?: string }) {
  const [page, setPage] = useState<PageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const tracked = useRef(false);
  const locale = props.locale || navigator.language || 'pt-BR';
  const slug = props.slug || '';
  const refCode = new URLSearchParams(window.location.search).get('ref') || '';

  useEffect(() => {
    fetch(`${BASE}/cms/entries/${encodeURIComponent(slug)}?contentType=solutions&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((d: any) => {
        const entry = d.data?.entry;
        if (entry) {
          // Map CMS fields to page shape
          const meta: any = (() => { try { return JSON.parse(entry.metadata || '{}'); } catch { return {}; } })();
          const cf: any = (() => { try { return JSON.parse(entry.custom_fields || '{}'); } catch { return {}; } })();
          entry.content = entry.body || entry.content || '';
          entry.subtitle = entry.excerpt || cf.description || '';
          entry.hero_image = meta.hero_image || '';
          entry.cta_url = meta.cta_url || '/register';
          entry.cta_text = cf.cta_text || '';
          entry.cta_style = meta.cta_style || 'primary';
          setPage(entry);
          document.title = entry.meta_title || entry.title || 'Solutions';
          // Set meta description
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) metaDesc.setAttribute('content', entry.meta_description || '');
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug, locale]);

  // Track view
  useEffect(() => {
    if (page && !tracked.current) {
      tracked.current = true;
      fetch(`${BASE}/cms/entries/${page.id}/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'view', locale }),
      }).catch(() => {});
    }
  }, [page]);

  const trackCta = () => {
    if (!page) return;
    fetch(`${BASE}/cms/entries/${page.id}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'cta_click', locale }),
    }).catch(() => {});
    const url = page.cta_url || '/register';
    window.location.href = url.startsWith('http') ? url : `window.location.origin${url}`;
  };

  if (loading) return <div style={S.loadingPage}><SkeletonBar width="60%" height={20} /></div>;
  if (error || !page) return (
    <div style={S.loadingPage}>
      <h2 style={{ color: T.text, fontSize: 24 }}>Pagina nao encontrada</h2>
      <a href={`/${locale}`} style={{ color: T.primary, marginTop: 16, display: 'inline-block' }}>Voltar</a>
    </div>
  );

  const features: string[] = (() => { try { return JSON.parse(page.features_json || '[]'); } catch { return []; } })();

  return (
    <div style={S.page}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .sol-cta:hover { transform: scale(1.05) !important; box-shadow: 0 8px 32px T.primary !important; }
        .sol-feature:hover { border-color: var(--t-white-alpha-15) !important; transform: translateY(-2px); }
        .sol-back:hover { opacity: 0.8; }
      `}</style>

      {/* Back link */}
      <a href={`/${locale}`} class="sol-back" style={S.backLink}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        {locale.startsWith('en') ? 'All solutions' : 'Todas as solucoes'}
      </a>

      {/* Hero */}
      <section style={S.hero}>
        <h1 style={S.heroTitle}>{page.title}</h1>
        <p style={S.heroSub}>{page.subtitle}</p>
        <Button variant="ghost" class="sol-cta" style={S.ctaBtn} onClick={trackCta}>
          {page.cta_text || 'Comece agora'}
        </Button>
      </section>

      {/* Features Grid */}
      {features.length > 0 && (
        <section style={S.featuresSection}>
          <h2 style={S.sectionTitle}>
            {locale.startsWith('en') ? 'Key Features' : 'Recursos Principais'}
          </h2>
          <div style={S.featuresGrid}>
            {features.map((f, i) => (
              <div key={i} class="sol-feature" style={{ ...S.featureCard, animationDelay: `${i * 0.08}s` }}>
                <div style={S.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5B8DEF" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d={featureIcons[i % featureIcons.length]} />
                  </svg>
                </div>
                <span style={S.featureText}>{f}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Content */}
      {page.content && (
        <section style={S.contentSection}>
          <div style={S.contentInner} dangerouslySetInnerHTML={{ __html: renderMarkdown(page.content) }} />
        </section>
      )}

      {/* Bottom CTA */}
      <section style={S.bottomCta}>
        <h2 style={S.bottomCtaTitle}>
          {locale.startsWith('en') ? 'Ready to get started?' : 'Pronto para comecar?'}
        </h2>
        <p style={S.bottomCtaSub}>
          {locale.startsWith('en')
            ? 'Join thousands of developers and teams already using WhiteLabelClaw.'
            : 'Junte-se a milhares de desenvolvedores e equipes que ja usam a WhiteLabelClaw.'}
        </p>
        <Button variant="ghost" class="sol-cta" style={S.ctaBtn} onClick={trackCta}>
          {locale.startsWith('en') ? 'Start for free' : 'Comece gratuitamente'}
        </Button>
      </section>
    </div>
  );
}

const S: Record<string, any> = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" },
  loadingPage: { minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' },
  spinner: { width: '60%', maxWidth: 300 },
  backLink: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '20px 24px', color: T.textSecondary, fontSize: 14, textDecoration: 'none', transition: 'opacity 0.15s' },
  hero: { textAlign: 'center', padding: '60px 24px 80px', maxWidth: 800, margin: '0 auto', background: 'linear-gradient(135deg, #1a1a4e 0%, #2d1b69 50%, #0a0e1a 100%)', borderRadius: '0 0 40px 40px', animation: 'fadeIn 0.6s ease-out' },
  heroTitle: { fontSize: 42, fontWeight: 800, margin: '0 0 20px', lineHeight: 1.2, background: 'linear-gradient(135deg, #E8EEF9, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  heroSub: { fontSize: 20, color: '#a0aec0', margin: '0 0 36px', lineHeight: 1.6, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' },
  ctaBtn: { display: 'inline-block', padding: '16px 40px', fontSize: 18, fontWeight: 700, borderRadius: 12, border: 'none', cursor: 'pointer', color: T.text, background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 4px 20px T.primaryBg' },
  featuresSection: { maxWidth: 1000, margin: '0 auto', padding: '60px 24px' },
  sectionTitle: { fontSize: 28, fontWeight: 700, textAlign: 'center', margin: '0 0 40px', color: T.text },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 },
  featureCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '24px', display: 'flex', alignItems: 'flex-start', gap: 16, transition: 'border-color 0.2s, transform 0.2s', animation: 'slideUp 0.5s ease-out both' },
  featureIcon: { minWidth: 44, height: 44, borderRadius: 10, background: T.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  featureText: { fontSize: 15, color: '#c4d0e9', lineHeight: 1.5 },
  contentSection: { maxWidth: 760, margin: '0 auto', padding: '20px 24px 60px' },
  contentInner: { fontSize: 16, lineHeight: 1.8, color: T.textSecondary },
  bottomCta: { textAlign: 'center', padding: '80px 24px', background: 'linear-gradient(180deg, transparent, T.primaryBg 50%, transparent)', borderTop: `1px solid ${T.bgCard}` },
  bottomCtaTitle: { fontSize: 32, fontWeight: 700, margin: '0 0 16px', color: T.text },
  bottomCtaSub: { fontSize: 18, color: T.textSecondary, margin: '0 0 36px', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 },
};
